import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Stethoscope, ArrowLeft, Copy, Trash2, Plus, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TemplatesPage() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTemplates();
  }, [doctorId]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates/${doctorId}`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = async (template) => {
    try {
      await axios.post(`${API}/templates/${template.id}/use`);
      
      // Navigate to script generator with pre-filled data
      navigate(`/script/${doctorId}`, {
        state: {
          templateData: {
            topic: template.template_name,
            format: template.format,
            hookStructure: template.hook_structure,
            explanationStructure: template.explanation_structure,
            ctaStructure: template.cta_structure
          }
        }
      });
      
      toast.success('Template loaded!');
    } catch (error) {
      console.error('Error using template:', error);
      toast.error('Failed to use template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#2C5F44] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#5C7063]">Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-texture">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(`/dashboard/${doctorId}`)}
              variant="ghost"
              size="sm"
              className="rounded-full"
              data-testid="templates-back-btn"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <Stethoscope className="w-8 h-8 text-[#2C5F44]" strokeWidth={1.5} />
            <span className="text-2xl font-bold text-[#2C5F44]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Content Templates
            </span>
          </div>
          <Button
            onClick={() => navigate(`/library/${doctorId}`)}
            className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full"
            data-testid="templates-create-btn"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Save from Library
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Your Content Templates
          </h1>
          <p className="text-lg text-[#5C7063]">
            Reuse successful script structures to create content faster
          </p>
        </motion.div>

        {templates.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center py-20"
          >
            <div className="w-24 h-24 bg-[#82A08B]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Copy className="w-12 h-12 text-[#2C5F44]" strokeWidth={1.5} />
            </div>
            <h2 className="text-3xl font-bold text-[#1C2A21] mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
              No Templates Yet
            </h2>
            <p className="text-lg text-[#5C7063] mb-8 max-w-2xl mx-auto">
              Save your best-performing scripts as templates to speed up future content creation
            </p>
            <Button
              onClick={() => navigate(`/library/${doctorId}`)}
              className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full px-8 py-6 text-lg"
              data-testid="templates-empty-create-btn"
            >
              Go to Content Library
            </Button>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {templates.map((template, index) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white rounded-3xl p-8 card-hover border border-[#2C5F44]/10"
                data-testid={`template-card-${index}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                      {template.template_name}
                    </h3>
                    <div className="flex gap-2 mb-4">
                      <Badge className="bg-[#82A08B]/20 text-[#2C5F44] rounded-full">
                        {template.format}
                      </Badge>
                      <Badge className="bg-[#D98A6C]/20 text-[#D98A6C] rounded-full">
                        {template.specialty}
                      </Badge>
                    </div>
                  </div>
                  {template.use_count > 0 && (
                    <div className="flex items-center gap-1 text-sm text-[#5C7063]">
                      <Zap className="w-4 h-4 text-[#D98A6C]" strokeWidth={1.5} />
                      {template.use_count}
                    </div>
                  )}
                </div>

                <div className="space-y-3 mb-6">
                  <div>
                    <p className="text-xs font-semibold text-[#5C7063] mb-1">HOOK STRUCTURE</p>
                    <p className="text-sm text-[#1C2A21] line-clamp-2">{template.hook_structure}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#5C7063] mb-1">EXPLANATION STRUCTURE</p>
                    <p className="text-sm text-[#1C2A21] line-clamp-2">{template.explanation_structure}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#5C7063] mb-1">CTA STRUCTURE</p>
                    <p className="text-sm text-[#1C2A21] line-clamp-1">{template.cta_structure}</p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleUseTemplate(template)}
                    className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full flex-1"
                    data-testid={`template-use-${index}`}
                  >
                    <Copy className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Use Template
                  </Button>
                  <Button
                    onClick={() => handleDeleteTemplate(template.id)}
                    variant="outline"
                    size="sm"
                    className="rounded-full border-[#2C5F44]/20"
                    data-testid={`template-delete-${index}`}
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Info Section */}
        {templates.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-12 bg-gradient-to-br from-[#2C5F44] to-[#244b36] rounded-3xl p-8 text-white"
          >
            <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Pro Tip: Save Your Best Performers
            </h2>
            <p className="text-white/90 mb-4">
              Templates help you maintain consistency and speed up content creation. 
              Save scripts that perform well as templates to replicate success across your content.
            </p>
            <Button
              onClick={() => navigate(`/library/${doctorId}`)}
              className="button-pill bg-white hover:bg-gray-100 text-[#2C5F44] rounded-full"
              data-testid="templates-info-library-btn"
            >
              Browse Content Library
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
